import { BandWorkspace, ExplorerItem, ExplorerItemId, Segment, TagOption } from "@/types/types.ts";
import fetchInstance from "@/services/fetch.ts";
import { IUser } from "@/interfaces/IUser.ts";
import { ITrack } from "@/interfaces/ITrack.ts";
import { GridRowId } from "@mui/x-data-grid-pro";

export function shareTrack(
  explorerItem: ExplorerItem,
  emailAddress: string,
  message: string,
  siteId: number,
  uniqueTabId: string,
) {
  try {
    return fetchInstance.post(
      `/track/${explorerItem.id}/share`,
      { emailAddress, message, uniqueTabId, explorerItemId: explorerItem.id, siteId },
    );
  } catch (error) {
    console.log("Fout bij het delen van de track:", error)

    throw error;
  }
}

export function fetchFullTrack(
  id: string,
) {
  try {
    // console.log('track.ts fetchFullTrack')
    return fetchInstance.get(`/full-item/${id}/get`).then(e => e.json());
  } catch (error) {
    console.log("Fout bij het de huidige track:", error)

    throw error;
  }
}

export function fetchTrackByPath(
  path: string,
) {
  try {
    return fetchInstance.get(`/track-by-path/${path}`).then(e => e.json());
  } catch (error) {
    console.log("Fout bij het de track:", error)

    throw error;
  }
}

export function apiTracksUploadFinished(
  playlistId: string,
  idList: string[],
  uniqueTabId: string,
) {
  return fetchInstance.post(
    `/track/upload-finished`,
    { playlistId, idList, uniqueTabId }
  )
}

export function updateCurrentTrack(
  id: ExplorerItemId,
) {
  return fetchInstance.put(
    `/track/current/${id}/update`,
    {},
  )
}

export function fetchNextTrack(explorerItemId: string | null) {
  if (!explorerItemId) {
    throw new Error('Warning: explorerItemId not defined');
  }

  try {
    return fetchInstance.get(`/track/get-next/${explorerItemId}`).then(e => e.json());
  } catch (error) {
    console.log("Fout bij het de volgende track:", error)

    throw error;
  }
}

export function fetchPreviousSegment(explorerItemId: ExplorerItemId, time: number) {
  if (!explorerItemId) {
    throw new Error('Warning: explorerItemId not defined');
  }

  if (isNaN(time) || time < 0) {
    throw new Error('Warning: Invalid time value');
  }

  const roundedTime = parseFloat(time.toFixed(1));

  try {
    return fetchInstance.get(`/segment/get-previous/${explorerItemId}/${roundedTime}`).then(e => e.json());
  } catch (error) {
    console.log("Fout bij het ophalen van het vorige segment:", error);
    throw error;
  }
}

export function fetchNextSegment(explorerItemId: ExplorerItemId, time: number) {
  if (!explorerItemId) {
    throw new Error('Warning: explorerItemId not defined');
  }

  if (isNaN(time) || time < 0) {
    throw new Error('Warning: Invalid time value');
  }

  const roundedTime = parseFloat(time.toFixed(1));

  try {
    return fetchInstance.get(`/segment/get-next/${explorerItemId}/${roundedTime}`).then(e => e.json());
  } catch (error) {
    console.log("Fout bij het ophalen van het volgende segment:", error);
    throw error;
  }
}

export function fetchPreviousTrack(explorerItemId: string | null) {
  if (!explorerItemId) {
    throw new Error('Warning: explorerItemId not defined');
  }

  try {
    return fetchInstance.get(`/track/get-previous/${explorerItemId}`).then(e => e.json());
  } catch (error) {
    console.log("Fout bij het ophalen van de vorige track (fetchPreviousTrackAction):", error)

    throw error;
  }
}

export function ssePauseTrack(
  uniqueTabId: string,
) {
  fetchInstance.post(`/track/pause`, { uniqueTabId });
}

export async function sseSeekTrackAction(
  id: ExplorerItemId,
  time: number,
  uniqueTabId: string,
) {
  return fetchInstance.post(`/track/seek/${id}`, { time, uniqueTabId });
}

export function ssePlayTrack(
  id: ExplorerItemId,
  uniqueTabId: string,
) {
  fetchInstance.post(`/track/play/${id}`, { uniqueTabId });
}

export function ssePlayPreviousTrack(
  id: ExplorerItemId,
  uniqueTabId: string,
) {
  fetchInstance.post(`/track/play-previous/${id}`, { uniqueTabId });
}

export function ssePlayNextTrack(
  id: ExplorerItemId,
  uniqueTabId: string,
) {
  fetchInstance.post(`/track/play-next/${id}`, { uniqueTabId });
}

export async function generateWaveform(
  track: ITrack,
) {
  return fetchInstance.post(
    `/track/${track.id}/waveform/generate`,
    {},
  )
}

export async function deleteTracks(
  explorerItemIds: string[],
  uniqueTabId: string,
) {
  try {
    return await fetchInstance.delete(
      '/tracks/delete',
      {
        explorerItemIds,
        uniqueTabId,
      },
    )
  } catch (error) {
    console.log("Fout bij deleteTracksAction:", error)

    throw error;
  }
}

export async function deleteTrack(
  explorerItemId: string,
  uniqueTabId: string,
) {
  try {
    return await fetchInstance.delete(
      `/track/delete/${explorerItemId}`,
      {
        uniqueTabId,
      },
    )
  } catch (error) {
    console.log("Fout bij deleteTrackAction:", error)

    throw error;
  }
}

export function userEventPlayingTrack(
  id: ExplorerItemId,
  user: IUser,
  uniqueTabId: string,
) {
  try {
    return fetchInstance.post(
      `/user/${user.id}/events/track/play/${id}`,
      { uniqueTabId },
    );
  } catch (error) {
    console.log("Fout bij het de huidige track:", error)

    throw error;
  }
}

export function renameTrack(
  trackId: string,
  name: string,
) {
  return fetchInstance.put(
    `/track/rename`,
    { name, trackId },
  )
}

export function updateSource(
  trackId: string,
  source: string,
) {
  return fetchInstance.put(
    `/track/update-source`,
    { source, trackId },
  )
}

export function duplicateTracks(
  explorerItemIds: string[],
  explorerItemPlaylistId: string,
  uniqueTabId: string,
) {
  return fetchInstance.post(
    `/tracks/duplicate/${explorerItemPlaylistId}`,
    {
      explorerItemIds,
      uniqueTabId,
    },
  )
}

export const createSegment = (
  id: ExplorerItemId,
  segment: Segment,
  uniqueTabId: string,
) => {
  return fetchInstance.post(
    `/track/${id}/segment/create`,
    {
      segment,
      uniqueTabId,
    },
  )
}

export const deleteSegment = (
  id: ExplorerItemId,
  segmentId: string,
  uniqueTabId: string,
) => {
  return fetchInstance.delete(
    `/track/${id}/segment/${segmentId}/delete`,
    {
      uniqueTabId,
    }
  )
}

export const deleteSegmentAudio = (
  id: ExplorerItemId,
  segmentId: string,
  uniqueTabId: string,
) => {
  return fetchInstance.delete(
    `/track/${id}/segment/audio/${segmentId}/delete`,
    {
      uniqueTabId,
    }
  )
}

export const cropSegmentAudio = (
  id: ExplorerItemId,
  segmentId: string,
  uniqueTabId: string,
  saveAsNewTrack = false,
) => {
  return fetchInstance.post(
    `/track/${id}/segment/audio/${segmentId}/crop/${saveAsNewTrack ? 'new' : 'self'}`,
    {
      uniqueTabId,
    }
  )
}

export const recordSegment = async (
  id: ExplorerItemId,
  segment: Segment,
  uniqueTabId: string,
) => {
  return fetchInstance.post(
    `/track/${id}/segment/record`,
    {
      segment,
      uniqueTabId,
    },
  )
}

export const segmentRecordFinished = (
  id: ExplorerItemId,
  segmentId: string,
  endTime: number,
  uniqueTabId: string,
) => {
  return fetchInstance.post(
    `/track/${id}/segment/${segmentId}/record-finished`,
    {
      endTime,
      uniqueTabId,
    },
  )
}

export const updateSegment = (
  id: ExplorerItemId,
  segment: Segment,
  uniqueTabId: string,
) => {
  const updatedSegment = {
    id: segment.id,
    startTime: segment.startTime,
    endTime: segment.endTime,
    labelText: segment.labelText,
    description: segment.description,
    editable: segment.editable || false,
    color: segment.color || '#FFFFFF',
  }

  return fetchInstance.post(
    `/track/${id}/segment/${segment.id}/update`,
    {
      segment: updatedSegment,
      uniqueTabId,
    },
  )
}

export const createSegmentWithConversation = (
  id: ExplorerItemId,
  segment: Segment,
  conversation: string | null,
  userConnectionIds: string,
  bandConnectionIds: string,
  uniqueTabId: string,
) => {
  return fetchInstance.post(
    `/track/${id}/segment/create`,
    {
      conversation,
      userConnectionIds,
      bandConnectionIds,
      segment,
      uniqueTabId,
    },
  )
}

export const updateSegmentWithConversation = (
  id: ExplorerItemId,
  segment: Segment,
  conversation: string | null,
  userConnectionIds: string,
  bandConnectionIds: string,
  uniqueTabId: string,
) => {
  const updatedSegment = {
    id: segment.id,
    startTime: segment.startTime,
    endTime: segment.endTime,
    labelText: segment.labelText,
    description: segment.description,
    editable: segment.editable || false,
    color: segment.color || '#FFFFFF',
  }

  return fetchInstance.post(
    `/track/${id}/segment/${segment.id}/update`,
    {
      userConnectionIds,
      bandConnectionIds,
      conversation,
      segment: updatedSegment,
      uniqueTabId,
    },
  )
}

export const createPointWithConversation = async (
  track: ITrack,
  userId: string,
  time: number | null,
  title: string,
  conversation: string | null,
  userConnectionIds: string,
  bandConnectionIds: string,
  uniqueTabId: string,
) => {
  const data = {
    time,
    userId,
    conversation,
    labelText: title,
    userConnectionIds,
    bandConnectionIds,
    uniqueTabId,
  }

  return fetchInstance.post(
    `/track/${track.id}/point/create`,
    data,
  )
}

export const updatePointWithConversation = async (
  track: ITrack,
  userId: string,
  pointId: string,
  time: number,
  title: string,
  conversation: string | null,
  bandConnectionIds: string,
  userConnectionIds: string,
  uniqueTabId: string,
) => {
  const data = {
    userId,
    time: time,
    labelText: title,
    conversation,
    bandConnectionIds,
    userConnectionIds,
    uniqueTabId,
  }

  return fetchInstance.post(
    `/track/${track.id}/point/${pointId}/update`,
    data,
  )
}

export async function changeExportedSegmentTracksOrder(
  segmentIds: number[],
  uniqueTabId: string,
) {
  return fetchInstance.post(
    `/explorer/exported-track-segments/change-order`,
    {
      segmentIds,
      uniqueTabId,
    }
  )
}

export async function changeExplorerItemsOrder(
  explorerItemIds: GridRowId[],
  uniqueTabId: string,
) {
  return fetchInstance.post(
    `/explorer/items/change-order`,
    {
      explorerItemIds,
      uniqueTabId,
    }
  )
}

export async function apiMoveItemsToFolder(
  selectedItemIds: ExplorerItemId[],
  explorerItemParentId: ExplorerItemId,
) {
  return fetchInstance.post(
    `/explorer/items/change-folder/${explorerItemParentId}`,
    {
      selectedItemIds,
      explorerItemParentId,
    },
  )
}

export async function apiMoveItemToFolder(
  explorerItemId: ExplorerItemId,
  explorerItemParentId: ExplorerItemId,
) {
  return fetchInstance.post(
    `/explorer/item/change-folder/${explorerItemId}/${explorerItemParentId}`,
    {
      explorerItemId,
      explorerItemParentId,
    },
  )
}

export async function fetchBandWorkspaceExplorerItemTags(bandWorkspace: BandWorkspace) {
  return await fetchInstance.get(`/band-workspace-explorer-item-tags/${bandWorkspace.id}`,)
}

export async function fetchExplorerItemTags(explorerItem: ExplorerItem) {
  return await fetchInstance.get(`/explorer-item-tags/${explorerItem.id}`,)
}

export async function createExplorerItemTag(
  {
    name,
    explorerItem,
    bandWorkspaceId,
    uniqueTabId,
  }:
  {
    name: string | TagOption,
    explorerItem: ExplorerItem,
    bandWorkspaceId: number,
    uniqueTabId: string,
  }) {
  return fetchInstance.post(
    `/create-explorer-item-tag/${explorerItem.id}`,
    { bandWorkspaceId, uniqueTabId, name },
  )
}

export async function deleteExplorerItemTag(
  {
    name,
    explorerItem,
    bandWorkspaceId,
    uniqueTabId,
  }:
  {
    name: string | TagOption,
    explorerItem: ExplorerItem,
    bandWorkspaceId: number,
    uniqueTabId: string,
  }) {
  return fetchInstance.delete(
    `/delete-explorer-item-tag/${explorerItem.id}`,
    { bandWorkspaceId, uniqueTabId, name },
  )
}

export async function deleteAllExplorerItemTags(
  {
    explorerItem,
  }:
  {
    explorerItem: ExplorerItem,
  }) {
  return fetchInstance.delete(`/delete-all-explorer-item-tags/${explorerItem.id}`)
}
